
import {
  ApplicationRef,
  ChangeDetectorRef,
  Inject,
  Injectable,
  Renderer2,
  RendererFactory2,
  DOCUMENT
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscribable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { LoggerService } from "./logger.service";

/**
 * This service handle generic base theme application operation
 */
@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private _logContext: string = "Theme.service";
  private _currentApplicationTheme: ApplicationTheme = null;
  private _themeSet: boolean = false;

  private _renderer: Renderer2 = null;

  /**
   * Get application them by configuration
   */
  get applicationTheme(): ApplicationTheme {
    return environment.theme as ApplicationTheme;
  }

  get isDigitalPlace(): boolean {
    return environment.theme === ApplicationTheme.DIGITAL_PLACE;
  }

  constructor(
    private _rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private _store: Store,
    private _ref: ApplicationRef,
    private _loggerService: LoggerService
  ) {
    this._currentApplicationTheme = environment.theme;

    this._themeSet = false;
    this._renderer = this._rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy() {
    this._themeSet = false;
  }

  /**
   * Check if the theme is Vimar in order to change icons in application
   */
  get isVimar(): boolean {
    return this.applicationTheme === ApplicationTheme.VIMAR;
  }

  /**
   * Set application them by configuration
   */
  public setApplicationTheme(cdApp: ChangeDetectorRef = null): void {
    this.setThemeByDistribution(environment.theme, cdApp);
  }

  /**
   * Get file name for backup
   */
  public getNameFile(): string {
    let nameFile: string = "";
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        nameFile = "bigstudio3_";
        break;

      case ApplicationTheme.DIGITAL_PLACE:
        nameFile = "digitalPlace_";
        break;

      case ApplicationTheme.BLUMOTIX:
        nameFile = "nemo_";
        break;
      case ApplicationTheme.SMART_HUB:
        nameFile = "smarthub_";
        break;
      case ApplicationTheme.DREX:
        nameFile = "drex_";
        break;
    }
    return nameFile;
  }

  /**
   * Get loader image path by application theme
   *
   * @returns
   */
  public getLogoApplicationByApplicatiomnTheme(): string {
    let srcApplicationLogo: string = "";

    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        srcApplicationLogo = "assets/img/logo_loader.png";
        break;

      case ApplicationTheme.DIGITAL_PLACE:
        srcApplicationLogo = "assets/img/digitalPlace/logo_digitalPlace.png";
        break;
      case ApplicationTheme.SMART_HUB:
        srcApplicationLogo = "assets/img/smartHub/logo_loader_smarthub.png";
        break;
      case ApplicationTheme.BLUMOTIX:
        srcApplicationLogo = "assets/img/blumotix/logo_loader_nemo.png";
        break;
      case ApplicationTheme.DREX:
        srcApplicationLogo = "assets/img/drex/logo_loader_drex.jpg";
        break;
      default:
        this._loggerService.log(
          LogLevel.LOG_WARNING,
          this._logContext,
          this._logContext,
          "getLogoApplicationByApplicatiomnTheme()",
          "NO application theme from store! Using default."
        );
        srcApplicationLogo = "assets/img/logo_loader.png";
        break;
    }

    return srcApplicationLogo;
  }

  /**
   * Get loader image path by application theme
   *
   * @returns
   */
  public getLogoLoaderByApplicationTheme(): string {
    let srcApplicationLogo: string = "";
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        srcApplicationLogo = "assets/img/logo_loader.png";
        break;

      case ApplicationTheme.DIGITAL_PLACE:
        srcApplicationLogo =
          "assets/img/digitalPlace/logo_loader_digitalPlace.png";
        break;
      case ApplicationTheme.BLUMOTIX:
        srcApplicationLogo = "assets/img/blumotix/logo_loader_nemo.png";
        break;
      case ApplicationTheme.SMART_HUB:
        srcApplicationLogo = "assets/img/smartHub/logo_loader_smarthub.png";
        break;
      case ApplicationTheme.DREX:
        srcApplicationLogo = "assets/img/drex/logo_loader_drex.jpg";
        break;
      case ApplicationTheme.UNKNOWN:
        srcApplicationLogo = "";
        break;
    }

    return srcApplicationLogo;
  }

  /**
   * Get the path of the extended brand logo, meant to be shown over a page surface (e.g. the
   * loading veil of the address search), as opposed to the loader one. Same mapping as BIGStudio
   *
   * @returns
   */
  public getBrandLogoByApplicationTheme(): string {
    return this.getLogoForLoginPage();
  }

  /**
   * Get menu pattern by application theme
   *
   * @param withLogo With logo inside image
   * @returns
   */
  public getPatternByApplicationTheme(withLogo: boolean = true): string {
    let srcPattern: string = "";

    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        if (withLogo) {
          srcPattern = "assets/img/BIGStudio_italycolor.png";
        } else {
          srcPattern = "assets/img/big_pattern_detail.jpg";
        }

        break;

      case ApplicationTheme.DIGITAL_PLACE:
        if (withLogo) {
          srcPattern = "assets/img/digitalPlace/logo_dp_transparent.png";
        } else {
          srcPattern = "assets/img/digitalPlace/pattern_no_logo.png";
        }
        break;

      case ApplicationTheme.BLUMOTIX:
        if (withLogo) {
          srcPattern = "assets/img/blumotix/smarthub_pattern_logo.png";
        } else {
          srcPattern = "assets/img/blumotix/smarthub_pattern_logo.png";
        }
        break;

      case ApplicationTheme.SMART_HUB:
        if (withLogo) {
          srcPattern = "assets/img/smartHub/smarthub_pattern_logo.png";
        } else {
          srcPattern = "assets/img/smartHub/smarthub_pattern_logo.png";
        }
        break;
      case ApplicationTheme.DREX:
        if (withLogo) {
          srcPattern = "assets/img/drex/drex_pattern_logo.png";
        } else {
          srcPattern = "assets/img/drex/drex_pattern_logo.png";
        }
        break;
    }

    return srcPattern;
  }

  /**
   * Get the color of toolbar
   * @returns
   */
  public getMainColorStatusBar(): string {
    let color: string = "";
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        color = "#1fa67a";
        break;

      case ApplicationTheme.DIGITAL_PLACE:
        color = "#3CA7BC";
        break;
    }
    return color;
  }

  /**
   * Get the color of toolbar when if is offline
   * @returns
   */
  public getMainColorStatusOffline(): string {
    let color: string = "";
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        color = "#2c3e50";
        break;

      case ApplicationTheme.DIGITAL_PLACE:
        color = "#222428";
        break;
    }
    return color;
  }

  /**
   * Get the color of timeslot selected
   * @returns
   */
  public getColorTimeSlotSelected(): string {
    let color: string = "";
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        color = "#ffeaa7";
        break;
    }
    return color;
  }

  /**
   * Get the color of timeslot unselected
   * @returns
   */
  public getColorTimeSlotUnselected(): string {
    let color: string = "";
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        color = "#a1ddb3";
        break;
    }
    return color;
  }
  //
  /**
   * Set applicatiomn system theme by app distribution name
   *
   * @param distributionName Distribution name
   */
  private setThemeByDistribution(
    applicationTheme: ApplicationTheme,
    cdApp: ChangeDetectorRef
  ): void {
    const themeString: string =
      EnumStringConverter.applicationThemeToStringEnum(applicationTheme);

    this._currentApplicationTheme = applicationTheme;

    this._renderer.removeClass(this.document.body, themeString);
    this._renderer.addClass(this.document.body, themeString);

    this.setDocumentTitleByDistribution();
    this.setDocumentFaviconByDistribution();

    if (cdApp) {
      cdApp.detectChanges();
    }
  }

  /**
   * Get the path for kigi to put in login page and in other pages with account form
   * @returns string for logo
   */
  public getLogoForLoginPage(): string {
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.BIG:
        return "assets/img/logo_semplice.png";
      case ApplicationTheme.BLUMOTIX:
        return "assets/img/blumotix/logo_nemo_simple.png";
      case ApplicationTheme.SMART_HUB:
        return "assets/img/smartHub/logo.png";
      case ApplicationTheme.DREX:
        return "assets/img/drex/logo.jpg";
      default:
        return "assets/img/digitalPlace/LogoDP.png";
    }
  }

  /**
   * Set document title by application distribution
   */
  private setDocumentTitleByDistribution(): void {
    switch (this._currentApplicationTheme) {
      case ApplicationTheme.DIGITAL_PLACE:
        this.document.title = "Digital Place";
        break;
      case ApplicationTheme.BLUMOTIX:
        this.document.title = "Nemo";
        break;
      case ApplicationTheme.SMART_HUB:
        this.document.title = "Smart Hub Italy";
        break;
      case ApplicationTheme.DREX:
        this.document.title = "Drex";
        break;
      default:
        this.document.title = "BIGStudio 3";
        break;
    }
  }

  /**
   * Set theme favicon
   */
  private setDocumentFaviconByDistribution(): void {
    const faviconElement: HTMLLinkElement =
      this.document.querySelector("#site-favicon");

    switch (this._currentApplicationTheme) {
      case ApplicationTheme.DIGITAL_PLACE:
        faviconElement.href = "assets/icon/favicon-digital-place.ico";
        break;

      case ApplicationTheme.BIG:
      default:
        faviconElement.href = "assets/icon/favicon.ico";
        break;

      case ApplicationTheme.BLUMOTIX:
        faviconElement.href = "assets/icon/favicon-nemo.ico";
        break;
      case ApplicationTheme.SMART_HUB:
        faviconElement.href = "assets/icon/favicon-smarthub.ico";
        break;
      case ApplicationTheme.DREX:
        faviconElement.href = "assets/icon/favicon-drex.ico";
        break;
    }
  }

}
